/* eslint-disable complexity */
import { sortDefectsWithUnkownDefect } from 'app/utils/defectSort';
import orderBy from 'lodash/orderBy';

/**
 * Pure confusion-matrix transforms.
 *
 * These functions contain the heavy O(D²·M) build and the O(D·M log M) sort
 * that used to run synchronously on the main thread inside the redux actions.
 * They are intentionally free of redux / DOM / i18next imports so they can run
 * inside a Web Worker (`src/workers/confusionMatrix.worker.js`).
 *
 * `store/aiPerformance/actions.js` re-uses these (and re-exports the sort
 * helpers wrapped with the i18next label) so existing call-sites are unchanged.
 *
 * Note: the cumulative "out of distribution" row label is injected as
 * `cumulativeRowLabel` instead of calling i18next `t()` — the worker thread has
 * no i18next instance, so translation stays on the main thread.
 */

export const CUMULATIVE_ROW_LABEL = 'Total out of distribution labels';

export const DEFAULT_TOTAL_DEFECT = {
  defect_name: CUMULATIVE_ROW_LABEL,
  name: CUMULATIVE_ROW_LABEL,
  organization_defect_code: '',
  formatted_name: CUMULATIVE_ROW_LABEL,
  is_trained_defect: false,
  id: -2,
  isCumulativeRow: true,
};

export const isMisclassificationPair = (gtDefect, aiDefect) => {
  return !(gtDefect === aiDefect || aiDefect === -1 || gtDefect === -1);
};

export const getTotalMisclassificationCount = data => {
  return data.reduce((prev, curr) => {
    if (isMisclassificationPair(curr.gt_defect, curr.ai_defect)) {
      return prev + curr.count;
    }
    return prev;
  }, 0);
};

const getConfusionRank = data => {
  const contributionMap = {
    '0-50': [],
    '50-80': [],
    '80+': [],
  };
  return data.map(({ misclassification_contribution_percent, ...rest }) => {
    if (!misclassification_contribution_percent) {
      return rest;
    }

    if (
      misclassification_contribution_percent <= 50 ||
      (misclassification_contribution_percent > 50 &&
        (contributionMap['0-50'][contributionMap['0-50'].length - 1] <= 50 ||
          contributionMap['0-50'].length === 0))
    ) {
      contributionMap['0-50'].push(misclassification_contribution_percent);
      return { ...rest, rank: 0 };
    }

    if (
      (misclassification_contribution_percent > 50 &&
        misclassification_contribution_percent <= 80) ||
      (misclassification_contribution_percent > 80 &&
        contributionMap['50-80'][contributionMap['50-80'].length - 1] < 80 &&
        contributionMap['50-80'][contributionMap['50-80'].length - 1] > 50)
    ) {
      contributionMap['50-80'].push(misclassification_contribution_percent);
      return { ...rest, rank: 1 };
    }

    if (misclassification_contribution_percent > 80) {
      contributionMap['80+'].push(misclassification_contribution_percent);
      return { ...rest, rank: 2 };
    }

    return rest;
  });
};

const getRankFromRawData = rawData => {
  const sortedData = orderBy(rawData, ['count'], ['desc']);
  const totalMisclassifications = getTotalMisclassificationCount(sortedData);

  let cumulativeSum = 0;
  const misclassificationContributionPercentage = sortedData.map(item => {
    let contributionPercentage = null;
    if (isMisclassificationPair(item.gt_defect, item.ai_defect)) {
      contributionPercentage =
        ((cumulativeSum + item.count) * 100) / totalMisclassifications;
      cumulativeSum += item.count;
    }

    return {
      ...item,
      misclassification_contribution_percent: contributionPercentage,
    };
  });

  return getConfusionRank(misclassificationContributionPercentage);
};

const generateMatrixFromRawData = rawData => {
  const matrix = {};
  const rawDataWithRanks = getRankFromRawData(rawData);
  rawDataWithRanks.forEach(item => {
    if (!matrix[item['gt_defect']]) {
      matrix[item['gt_defect']] = { ai_defects: {} };
    }
    matrix[item['gt_defect']]['ai_defects'][item['ai_defect']] = item;
  });

  return matrix;
};

const generateMatrixMetaFromMatrix = (matrix, defects, rawData) => {
  const matrixMeta = {};
  const DEFAULT_META_VALUE = {
    recall: 0,
    precision: 0,
    model_count: 0,
    gt_count: 0,
  };

  const outOfDistributionDefects = [];

  defects.forEach(item => {
    if (!matrixMeta[item.id]) {
      matrixMeta[item.id] = { ...DEFAULT_META_VALUE };
    }

    if (!item.is_trained_defect) {
      outOfDistributionDefects.push(item);
    }
  });

  // Cumulative Out of Distribution Data
  matrixMeta['-2'] = { ...DEFAULT_META_VALUE, recall: 'N/A', precision: 'N/A' };

  const unknownAiDefectMap = {};

  rawData.forEach(item => {
    if (item.gt_defect === -1) {
      unknownAiDefectMap[item.ai_defect] =
        (unknownAiDefectMap[item.ai_defect] || 0) + item.count;
    }
  });

  rawData.forEach(item => {
    const { model_count, ...rest } = matrixMeta[item.ai_defect];
    matrixMeta[item.ai_defect] = {
      ...rest,
      model_count: model_count + item.count,
    };
  });

  Object.keys(matrix).forEach(item => {
    let unknownGTDefectCount = 0;
    matrixMeta[item].gt_count = Object.values(matrix[item].ai_defects).reduce(
      (prev, curr) => {
        if (curr.ai_defect === -1) {
          unknownGTDefectCount += curr.count;
        }
        return prev + curr.count;
      },
      0
    );

    const actualGTCount = matrixMeta[item].gt_count - unknownGTDefectCount;

    matrixMeta[item].recall =
      actualGTCount && Number(item) !== -1
        ? ((matrix[item].ai_defects[item]?.count || 0) * 100) / actualGTCount
        : 'N/A';

    const actualModelCount =
      (matrixMeta[item].model_count || 0) - (unknownAiDefectMap[item] || 0);

    matrixMeta[item].precision =
      actualModelCount && Number(item) !== -1
        ? ((matrix[item].ai_defects[item]?.count || 0) * 100) / actualModelCount
        : 'N/A';
    matrixMeta[item].gt_count = Object.values(matrix[item].ai_defects).reduce(
      (prev, curr) => prev + curr.count,
      0
    );
  });

  matrixMeta['-2'].gt_count = outOfDistributionDefects.reduce(
    (prev, curr) => prev + matrixMeta[curr.id].gt_count,
    0
  );

  return matrixMeta;
};

export const getConfusionMatrix = (rawData, defects) => {
  const matrix = generateMatrixFromRawData(rawData);
  const matrixMeta = generateMatrixMetaFromMatrix(matrix, defects, rawData);

  return { matrix, matrixMeta };
};

/**
 * Builds the redux `data` payload ({ matrix, matrixMeta }) for the confusion
 * matrix from the raw API response. This is the heavy O(D²·M) step.
 */
export function buildConfusionMatrixPayload(payload) {
  const { confusion_matrix, defects } = payload;

  if (!confusion_matrix.length) {
    return { matrix: [], matrixMeta: {} };
  }

  const inDistributionDefect = Object.values(defects).filter(
    item => item.is_trained_defect
  );
  const { matrix: formatedMatrix, matrixMeta } = getConfusionMatrix(
    confusion_matrix,
    Object.values(defects)
  );

  const result = [];
  Object.values(defects).forEach(defect => {
    let ai = {};
    const isTrainedDefect = defect.is_trained_defect;
    let isEmptyRow = !isTrainedDefect;
    if (formatedMatrix[defect.id]) {
      ai = { ...formatedMatrix[defect.id], ai_defects: [], defect };
      const originalPredictions = formatedMatrix[defect.id].ai_defects;
      inDistributionDefect.forEach(predicted => {
        if (!originalPredictions[predicted.id]) {
          ai.ai_defects.push({
            defect: predicted,
            count: 0,
            file_set_ids: [],
          });
        } else {
          if (originalPredictions[predicted.id].count > 0) {
            isEmptyRow = false;
          }
          ai.ai_defects.push({
            defect: predicted,
            ...originalPredictions[predicted.id],
          });
        }
      });
    } else {
      ai = {
        ...ai,
        defect,
        gt_count: 0,
        ai_defects: inDistributionDefect.map(defect => ({
          defect,
          count: 0,
          file_set_ids: [],
        })),
      };
    }
    if (!isEmptyRow) {
      result.push(ai);
    }
  });

  return { matrix: result, matrixMeta };
}

export const sortNormalizedDefect = (order, defects) => {
  return sortDefectsWithUnkownDefect(
    defects,
    ['defect.organization_defect_code', 'defect.name'],
    order === 'asc'
  ).map(gtDefect => {
    return {
      ...gtDefect,
      ai_defects: sortDefectsWithUnkownDefect(
        gtDefect.ai_defects,
        ['defect.organization_defect_code', 'defect.name'],
        order === 'asc'
      ),
    };
  });
};

const getTotalOutOfDistribution = (data, cumulativeRowLabel) => {
  if (!data.length) {
    return [];
  }

  const { ai_defects } = data[0];

  const cumulativeAiDefects = [];

  ai_defects.forEach((_, aiDefectIndex) => {
    let totalCount = 0;

    data.forEach((_, dataIndex) => {
      totalCount += data[dataIndex]['ai_defects'][aiDefectIndex]?.count || 0;
    });
    const { defect } = data[0]['ai_defects'][aiDefectIndex];
    cumulativeAiDefects.push({ defect, count: totalCount });
  });

  return [
    {
      defect: {
        ...DEFAULT_TOTAL_DEFECT,
        name: cumulativeRowLabel ?? DEFAULT_TOTAL_DEFECT.name,
      },
      ai_defects: cumulativeAiDefects,
    },
  ];
};

export const sortDefectsForMatrix = (
  order,
  defects,
  isCollapsed,
  cumulativeRowLabel
) => {
  const sortedInDistribution = sortNormalizedDefect(
    order,
    defects.filter(item => item.defect.is_trained_defect)
  );

  const sortedOutOfDistribution = sortNormalizedDefect(
    order,
    defects.filter(item => !item.defect.is_trained_defect)
  );

  if (isCollapsed && sortedOutOfDistribution.length) {
    return {
      inDistribution: sortedInDistribution,
      outOfDistribution: getTotalOutOfDistribution(
        sortedOutOfDistribution,
        cumulativeRowLabel
      ),
    };
  }
  return {
    inDistribution: sortedInDistribution,
    outOfDistribution: [
      ...getTotalOutOfDistribution(sortedOutOfDistribution, cumulativeRowLabel),
      ...sortedOutOfDistribution,
    ],
  };
};
